import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Checkbox } from "@mui/material";
import API_BASE_URL from "../../../../Constants";
import { ColorRing } from 'react-loader-spinner';

const ManageDevicesModal = ({ open, handleClose, phaseId, allBuildingDevices, viewOnly = false }) => {
    const [assignedDevices, setAssignedDevices] = useState([]);
    const [selectedDevices, setSelectedDevices] = useState([]);

    const [loadingPhaseDevices, setLoadingPhaseDevices] = useState(false);

    useEffect(() => {
        if (open && phaseId) {
            fetchAssignedDevices();
        }
    }, [open, phaseId]);

    // Fetch already assigned devices for the phase
    const fetchAssignedDevices = async () => {
        try {
            setLoadingPhaseDevices(true);
            const response = await fetch(`${API_BASE_URL}/api/proposals-and-jobs/phases/${phaseId}/devices`);
            if (!response.ok) throw new Error("Failed to fetch assigned devices");

            const data = await response.json();
            const assignedDeviceIds = data.map((device) => device.deviceId);
            setAssignedDevices(assignedDeviceIds);
            setSelectedDevices(assignedDeviceIds); // Pre-select assigned devices
        } catch (error) {
            console.error("Error fetching assigned devices:", error);
        }
        finally {
            setLoadingPhaseDevices(false);
        }
    };

    // Handle checkbox selection
    const handleDeviceSelection = (deviceId) => {
        setSelectedDevices((prevSelected) =>
            prevSelected.includes(deviceId)
                ? prevSelected.filter((id) => id !== deviceId) // Remove if already selected
                : [...prevSelected, deviceId] // Add if not selected
        );
    };

    // Save selected devices
    const handleSaveDevices = async () => {
        try {
            const devicesToAdd = selectedDevices.filter((id) => !assignedDevices.includes(id));
            const devicesToRemove = assignedDevices.filter((id) => !selectedDevices.includes(id));

            // Add new devices
            if (devicesToAdd.length > 0) {
                await fetch(`${API_BASE_URL}/api/proposals-and-jobs/phases/${phaseId}/devices/batch`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(devicesToAdd.map((deviceId) => ({ phaseId, deviceId }))),
                });
            }

            // Remove devices
            for (const deviceId of devicesToRemove) {
                await fetch(`${API_BASE_URL}/api/proposals-and-jobs/phases/${phaseId}/devices/${deviceId}`, {
                    method: "DELETE",
                });
            }

            setAssignedDevices(selectedDevices); // Update assigned devices
            handleClose();
        } catch (error) {
            console.error("Error updating devices:", error);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>{viewOnly ? "View" : "Manage"} Devices for Phase #{phaseId}</DialogTitle>

            {
                loadingPhaseDevices ?
                    (
                        <div className="flex justify-center items-center h-full">
                            <ColorRing color="#333" />
                        </div>
                    )
                    : (
                        <>
                            <DialogContent>
                                <table className="w-full border-collapse border border-gray-300">
                                    <thead>
                                        <tr className="bg-gray-200">
                                            <th className="border p-2">Select</th>
                                            <th className="border p-2">Device ID</th>
                                            <th className="border p-2">Device Name</th>
                                            <th className="border p-2">Device Type</th>
                                        </tr >
                                    </thead >
                                    <tbody>
                                        {allBuildingDevices.map((device) => (
                                            <tr key={device.deviceId} className="border">
                                                <td className="border p-2 text-center">
                                                    <Checkbox
                                                        checked={selectedDevices.includes(device.deviceId)}
                                                        onChange={() => handleDeviceSelection(device.deviceId)}
                                                        disabled={viewOnly}
                                                    />
                                                </td>
                                                <td className="border p-2">{device.deviceId}</td>
                                                <td className="border p-2">{device.deviceName || "N/A"}</td>
                                                <td className="border p-2">{device.deviceType || "N/A"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table >

                            </DialogContent >
                            <DialogActions>

                                <Button onClick={handleClose} color="primary">
                                    {viewOnly ? "Close" : "Cancel"}
                                </Button>
                                {!viewOnly && <>
                                    <Button onClick={handleSaveDevices} color="success" variant="contained">
                                        Save
                                    </Button>
                                </>}
                            </DialogActions>
                        </>
                    )
            }


        </Dialog >
    );
};

export default ManageDevicesModal;
